import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';

import { TranslatePipe } from '@ngx-translate/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';

import { ChatNavigationService } from '@lib/chat/core/services/chat-navigation.service';
import { CHAT_NAVIGATION_HANDLERS } from '@lib/chat/core/tokens/chat-navigation.tokens';
import { ChatStateService } from '@lib/chat/services/chat-state.service';

import { formatDate } from '@lib/commons/util-functions/format-date.function';

import { PrescriptionResponse } from '@lib/core/models/POC-prescription.interface';
import { CreateAppointmentModalType } from '@lib/core/models/create-appointment.interface';
import { MemberProfile } from '@lib/core/models/member.interface';
import { PrescriptionHistoryStatus, PrescriptionTypes } from '@lib/core/models/prescription.interface';

import { StatusComponent } from '@lib/shared-ui/commons/status/status.component';

enum PrescriptionTypeFilters {
  All = 'All',
  Appointment = 'Appointment',
  Drugs = 'Drugs',
  InstrumentalDiagnostic = 'InstrumentalDiagnostic',
  LaboratoryService = 'LaboratoryService',
}

@Component({
  selector: 'chat-prescriptions-list',
  standalone: true,
  imports: [NgClass, MatButtonModule, MatIcon, MatMenu, MatMenuItem, MatMenuTrigger, TranslatePipe, StatusComponent],
  styles: `
    :host {
      @apply h-full;
    }
  `,
  templateUrl: './chat-prescription-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatPrescriptionListComponent {
  private readonly navigationHandlers = inject(CHAT_NAVIGATION_HANDLERS);
  private readonly chatState = inject(ChatStateService);
  private readonly chatNavigationService = inject(ChatNavigationService);

  protected readonly formatDate = formatDate;

  member = input.required<MemberProfile>();

  protected readonly selectedTypeFilter = signal<PrescriptionTypeFilters>(PrescriptionTypeFilters.All);
  protected readonly loading = signal<boolean>(false);
  protected readonly highlightedPrescriptionId = signal<string | null>(null);

  protected readonly filteredPrescriptions = computed<PrescriptionResponse[]>(() => {
    const prescriptions = this.chatState.prescriptions();
    const typeFilter = this.mapFilterToType(this.selectedTypeFilter());
    let filtered = prescriptions.filter(item => item.historyStatus === PrescriptionHistoryStatus.Active);

    if (this.selectedTypeFilter() !== PrescriptionTypeFilters.All && typeFilter) {
      filtered = filtered.filter(prescription => prescription.prescriptionType === typeFilter);
    }

    return filtered;
  });

  constructor() {
    effect(() => {
      const targetPrescriptionId = this.chatNavigationService.targetPrescriptionId();
      if (targetPrescriptionId) {
        this.highlightPrescription(targetPrescriptionId);
        setTimeout(() => this.chatNavigationService.clearTargetPrescription(), 200);
      }
    });
  }

  protected setTypeFilter(filter: keyof typeof PrescriptionTypeFilters): void {
    this.selectedTypeFilter.set(PrescriptionTypeFilters[filter]);
  }

  protected createAppointment(prescription: PrescriptionResponse, event: Event): void {
    event.stopPropagation();
    this.navigationHandlers.openCreateManualAppointmentModal?.(this.member().id, CreateAppointmentModalType.ManualCreation, {
      prescriptionId: prescription.id,
      prescriptionName: prescription.prescriptionName,
    });
  }

  protected bindToExistingAppointment(prescription: PrescriptionResponse, event: Event): void {
    event.stopPropagation();
    this.navigationHandlers.openCreateManualAppointmentModal?.(this.member().id, CreateAppointmentModalType.BindToExisting, {
      prescriptionName: prescription.prescriptionName,
      prescriptionId: prescription.id,
    });
  }

  protected navigateToPrescription(prescription: PrescriptionResponse, event: Event): void {
    event.stopPropagation();
    this.navigationHandlers.navigateToPrescription?.(prescription.id, this.member().userId);
  }

  protected getPrescriptionIcon(prescriptionType: PrescriptionTypes): string {
    switch (prescriptionType) {
      case PrescriptionTypes.Drugs:
        return 'vaccines';
      case PrescriptionTypes.InstrumentalDiagnostic:
        return 'biotech';
      case PrescriptionTypes.LaboratoryService:
        return 'science';
      case PrescriptionTypes.RelatedSpecialists:
        return 'group';
      default:
        return 'medication';
    }
  }

  private mapFilterToType(filter: PrescriptionTypeFilters): PrescriptionTypes | null {
    switch (filter) {
      case PrescriptionTypeFilters.Drugs:
        return PrescriptionTypes.Drugs;
      case PrescriptionTypeFilters.InstrumentalDiagnostic:
        return PrescriptionTypes.InstrumentalDiagnostic;
      case PrescriptionTypeFilters.LaboratoryService:
        return PrescriptionTypes.LaboratoryService;
      case PrescriptionTypeFilters.Appointment:
        return PrescriptionTypes.Appointment;
      default:
        return null;
    }
  }

  public highlightPrescription(prescriptionId: string): void {
    console.log('Highlighting prescription:', prescriptionId);

    const prescriptionExists = this.filteredPrescriptions().some(p => p.id === prescriptionId);
    if (!prescriptionExists) this.setTypeFilter('All');

    this.highlightedPrescriptionId.set(prescriptionId);

    // Use requestAnimationFrame for DOM updates, then setTimeout for scroll and cleanup
    requestAnimationFrame(() => {
      const element = document.querySelector(`[data-prescription-id="${prescriptionId}"]`);

      if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      else console.log('Prescription element not found in DOM');
    });
    // Clean removal after 5 seconds
    setTimeout(() => this.highlightedPrescriptionId.set(null), 5000);
  }
}
